import { useMemo } from 'react';
import { useAddress, useStore } from 'hooks';
import { type Qual } from 'types';

/**
 * Hook that checks if the initial (visiting) qual has a zip but no street address.
 * Uses visitingQual to ensure we're checking the original qual, not the working qual
 * which may have been modified by setAddress() or other mutations.
 * Useful for functionality based on the initial qual data, like routing.
 *
 * @returns true if initial qual has zip but no street
 */
export function useIsZipOnlyQual(): boolean {
  const visitingQual = useStore((state) => state.visitingQual);

  const isZipOnlyQual = useMemo(() => {
    if (!visitingQual) {
      return false;
    }

    try {
      const decodedQual = atob(visitingQual);
      const qual: Qual = JSON.parse(decodedQual);

      if (!qual || typeof qual !== 'object') {
        console.warn('Invalid qual object structure', decodedQual);
        return false;
      }

      const hasZip = Boolean(qual.zip);
      const hasStreet = Boolean(qual.street);

      return hasZip && !hasStreet;
    } catch (error) {
      console.error('Failed to parse visiting qual:', error);
      return false;
    }
  }, [visitingQual]);

  return isZipOnlyQual;
}

/**
 * Hook that checks if the address has a postal code but no street address.
 * Useful for functionality based on the current address data, like displaying info.
 *
 * @returns true if address has postalCode but no street
 */
export function useIsZipOnlyAddress(): boolean {
  const address = useAddress();
  return Boolean(address?.postalCode) && !address?.street;
}
